import { useLayoutEffect, useMemo, useRef } from 'react';
import { Container, Stage } from '@pixi/react';
import { observer } from 'mobx-react-lite';

import { PascalFortunePotsGameId, PascalGodOfAtlantisGameId, PascalSugarBalloonGameId } from '../../../../../constants';
import { AspectRatioImage } from '../../../../../view/global/features/cashback/ratio-image';
import { BoostedJackpotIconSliderBoardWrapper } from './boostedJackpotIconSliderBoardWrapper';
import { UserActivityCategory, UserActivityTypes } from '@pascal/iframe-service';

import { stageOptions } from '../../../frameModule/constants/gameConstants';
import { loadingStore } from '../../../frameModule/blocks/loading';
import { BoostedJackpotIconView } from './boostedJackpotIconView';
import { Providers } from '../../../../../view/containers';

import { getGameImages } from '../../../../helpers';
import { isMobile } from '../../../../../helpers';
import { useStore } from '../../../../../hooks';

export const BoostedJackpotIcon: React.FC = observer(() => {
  const { boostedJackpotStore, gameStore, userActivityStore } = useStore();
  const { stakeAmount, canvasWidth } = gameStore;
  const { boostedBetAmount } = boostedJackpotStore;
  const gameImages = getGameImages();

  const boostedJackpotIconInfoBoard =
    gameImages['boostedJackpotIconInfoBoard.png'] || gameImages.boostedJackpotIconInfoBoard;
  const boostedJackpotIconInfoBoardRectangle =
    gameImages['boostedJackpotIconInfoRectangle.png'] || gameImages.boostedJackpotIconInfoBoardRectangle;

  const animationName = useMemo(() => {
    if (boostedBetAmount) {
      return stakeAmount >= boostedBetAmount ? 'active' : 'idle';
    }
    return 'idle';
  }, [stakeAmount, boostedBetAmount]);

  const isAllResourceLoaded = loadingStore.isAllResourceLoaded;
  const { boostedJackpotResourcesLoaded } = boostedJackpotStore;
  const boostedWidgetRefApp = useRef<any>(null);


  useLayoutEffect(() => {
    boostedWidgetRefApp?.current?.app.renderer.resize(250, isMobile() ? 75 : 110);
  }, [isAllResourceLoaded, boostedJackpotResourcesLoaded]);

  const SIZE = 250;
  const isWebWithCanvas = !isMobile() && !!canvasWidth;
  const useMobileLayout = isMobile() || !!canvasWidth;

  const { containerConfigBoostedJackpotIcon, containerConfigBoostedJackpotIconSliderBoard } = {
    containerConfigBoostedJackpotIcon: { x: useMobileLayout ? -63 : 68, y: useMobileLayout ? 0 : -71 },
    containerConfigBoostedJackpotIconSliderBoard: { x: useMobileLayout ? 0 : -100, y: useMobileLayout ? 40 : -75 },
  };
  const isAtlantisGame = gameStore.gameId === PascalGodOfAtlantisGameId;
  const isFortunePotsGame = gameStore.gameId === PascalFortunePotsGameId;
  const isSugarBalloonGame = gameStore.gameId === PascalSugarBalloonGameId;
  const handlePointerUp = () => {
    userActivityStore.sendTrackUserActivity({
      category: UserActivityCategory.PromoTools,
      type: UserActivityTypes.BoostedJackpotWidget
    });
    boostedJackpotStore.setIsBoostedJackpotInfoPopup(true);
  };
  if (!boostedJackpotResourcesLoaded) return null;
  const content = (
    <div
      style={isWebWithCanvas ? { maxWidth: canvasWidth } : {}}
      className={`bj-widget ${isAtlantisGame ? 'atlantis' : isFortunePotsGame ? 'fortune' : isSugarBalloonGame ? 'sugarBalloon' : ''}`}
    >
      <AspectRatioImage />
      <div
        className='mini-wheel-canvas'
      >
        <Stage
          id='boosted-jackpot-mini-wheel'
          ref={boostedWidgetRefApp}
          width={useMobileLayout ? 200 : SIZE}
          height={useMobileLayout ? 100 : SIZE}
          options={{
            ...stageOptions,
            backgroundAlpha: 0,
            antialias: true
          }}
        >
          <Providers>
            <Container x={useMobileLayout ? 100 : SIZE / 2} y={useMobileLayout ? 40 : SIZE / 2} {...({ name: 'BoostedJackpotWidgetContainer' } as any)}>
              <BoostedJackpotIconSliderBoardWrapper boostedJackpotIconInfoBoardRectangle={boostedJackpotIconInfoBoardRectangle} containerConfigBoostedJackpotIconSliderBoard={containerConfigBoostedJackpotIconSliderBoard} />
              <Container {...containerConfigBoostedJackpotIcon as any}>
                <BoostedJackpotIconView
                  isDisabled={!stakeAmount}
                  animationName={animationName}
                  handlePointerUp={handlePointerUp}
                  boostedJackpotIconInfoBoard={boostedJackpotIconInfoBoard}
                />
              </Container>
            </Container>
          </Providers>
        </Stage>
      </div>
    </div>
  );

  if (isWebWithCanvas) {
    return <div className="is-mobile bj-canvas-mobile">{content}</div>;
  }

  return content;
});
